import { useState, useEffect, useRef, useCallback } from 'react'
import moment from 'moment'
import { useTheme } from '@mui/material/styles'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'

import SaveModalDataContent from 'components/Reporting/Overview/FilterBuilder/FilterSet/FilterDialogue/SaveContent'
import CustomButton from 'components/Shared/Button/index'
import usePrevious from 'lib/hooks/UsePrevious'
import { useAppSelector, useAppDispatch } from 'redux/Hook'
import {
  setSaveFilterActive,
  setSharableFilterLink,
  setSharedModalFlow,
} from 'redux/slices/FilterBuilder'
import { AdminUITheme } from 'types/AdminUiThemeMui'
import {
  filterValues,
  filterUpdatesForJSONCheck,
} from 'containers/Reporting/Shared/Helper'
import CustomDialog from 'components/Shared/Dialog'

const SaveFiltersBtn = ({
  testProps = false,
  onSaveFilterSet,
  selectedIndex,
}: any) => {
  const dispatch = useAppDispatch()
  const theme: AdminUITheme = useTheme()
  const [isDisabled, setDisabled] = useState(true)
  const [isUpdated, setUpdated] = useState(false)
  const [modalVisibility, setModalVisibility] = useState(false)
  const [memoisedFilter, setMemoisedFilter] = useState([])
  const [filterData, setFilterData] = useState([])
  const { t } = useTranslation()
  const [savedFilter, setSavedFilter] = useState('')

  const {
    filterBuilder: {
      selectedFilters = [],
      savedSelectionFilters = [],
      savedFilter: { filterName: savedFilterName = '' } = {},
      savedFilter: savedFilterProps = {},
      isSharable = false,
    } = {},
  }: any = useAppSelector((state) => state)
  const userFilters = filterValues(selectedFilters, true)

  const deepEqual = (obj1: any, obj2: any) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2)
  }

  // update date selection since we dont want to compare timestamp
  const updatedValueFilters = useCallback((data?: any) => {
    const value = data?.map(
      (item: any) =>
        Array.isArray(item) &&
        item?.map((k: any) => {
          return Object.keys(k)?.includes('endDate')
            ? (k = {
                ...(!k?.dateRangeOption && {
                  startDate: moment(k?.startDate)?.format('DD/MM/YY'),
                  endDate: moment(k?.endDate)?.format('DD/MM/YY'),
                  key: k?.key || 'selection',
                }),
                dateRangeOption: k?.dateRangeOption || 'custom',
              })
            : k
        }),
    )

    return filterUpdatesForJSONCheck(value)
  }, [])

  const prevProps: any = usePrevious({
    updatedValueFilters,
    savedSelectionFilters,
    userFilters,
  })
  const inputRef: any = useRef('')
  const setUserInput = (input: any) => {
    inputRef.current = input
  }
  const saveModalProps = {
    setUserInput,
    userInput: inputRef.current,
    t,
    savedSelectionFilters,
    savedFilter,
  }

  const onSave = useCallback(
    async (isSharableLink) => {
      const { textValue: { text: filterName = '' } = {}, updateValue } =
        inputRef?.current || {}

      if (!filterName?.length) {
        updateValue({
          isError: true,
          text: filterName,
          validation: t('reporting.filterSet.requiredFiled'),
          position: 'left',
        })
      } else {
        const result = await onSaveFilterSet(
          inputRef,
          selectedFilters,
          savedFilterProps,
        )

        if (result) {
          isSharableLink && dispatch(setSharedModalFlow(true))
          dispatch(setSharableFilterLink(false))
          setModalVisibility(false)
          setDisabled(result)
        }
      }
    },
    [selectedFilters, savedFilter],
  )

  const customDialogProps = {
    title: t('reporting.filterSet.saveFilterSet'),
    showCloseBtn: true,
    enableActionButtons: true,
    content: <SaveModalDataContent {...saveModalProps} />,
    onCloseModal: () => {
      setModalVisibility(false)
      dispatch(setSharableFilterLink(false))
    },
    isModalOpen: modalVisibility,
    primaryBtn: (
      <CustomButton
        title={
          isSharable
            ? t('reporting.filterSet.saveShare')
            : t('reporting.filterSet.save')
        }
        btnVariant="contained"
        onClick={() => {
          onSave(isSharable)
        }}
      />
    ),
    secondaryBtn: (
      <CustomButton
        title={t('reporting.filterSet.cancel')}
        disabled={false}
        onClick={() => {
          setModalVisibility(false)
          dispatch(setSharableFilterLink(false))
        }}
      />
    ),
    ...(testProps && {
      testProps: {
        disablePortal: true,
        hideBackdrop: true,
        TransitionComponent: ({ children }: any) => children,
      },
    }),
  }

  const handleClick = () => {
    setModalVisibility(true)
  }

  useEffect(() => {
    setUpdated(false)
  }, [])

  useEffect(() => {
    const value = updatedValueFilters(userFilters)
    setFilterData(value)
  }, [selectedFilters])

  useEffect(() => {
    const updateSaveSelection = () => {
      if (!isEmpty(memoisedFilter) && !deepEqual(memoisedFilter, filterData)) {
        setDisabled(false)
      } else {
        setDisabled(true)
      }
    }

    updateSaveSelection()
  }, [filterData, memoisedFilter])

  useEffect(() => {
    const filterUpdate = async () => {
      if (!isUpdated && !isEmpty(userFilters) && selectedIndex === -1) {
        setUpdated(true)
        const updatedFilters = updatedValueFilters(userFilters)
        setMemoisedFilter(updatedFilters)
      } else if (
        !deepEqual(prevProps?.savedSelectionFilters, savedSelectionFilters)
      ) {
        setUpdated(false)
        const savedUserFilters = await filterValues(savedSelectionFilters, true)
        const updatedFilters = updatedValueFilters(savedUserFilters)
        setMemoisedFilter(updatedFilters)
      }
    }

    filterUpdate()
  }, [userFilters, prevProps?.savedSelectionFilters, savedSelectionFilters])

  useEffect(() => {
    setSavedFilter(savedFilterName)
  }, [savedFilterName])

  useEffect(() => {
    /** if share link btn is clicked */
    setModalVisibility(isSharable)
  }, [isSharable])

  useEffect(() => {
    dispatch(setSaveFilterActive(isDisabled))
  }, [isDisabled])

  return (
    <>
      <CustomButton
        id="filter-group"
        onClick={handleClick}
        size="large"
        data-testid="filter-group"
        disabled={isDisabled}
        style={theme?.ParticipantGrid?.paddingTopPointFive}
        iconName="save"
        title={t('reporting.filterSet.save')}
      ></CustomButton>

      <CustomDialog {...customDialogProps} />
    </>
  )
}

export default SaveFiltersBtn
